import Head from 'next/head';
import { organization } from 'lib/seo/constants';
import useDictionary from 'lib/hooks/useDictionary';
import { useOembed } from 'lib/seo/useOembed';

interface IHowToSchemaProps {
  time?: {
    totalTimeHours: string;
    totalTimeMinutes: string;
  };
  supplies: {
    suppliesList: {
      suppliesQuantity: string;
      suppliesName: string;
    }[];
  }[];
  steps: {
    stepsSubheading: string;
    stepsList: {
      stepDescription: string;
    }[];
  }[];
  notes?: {
    noteItem: string;
  }[];
  title: string;
  imageUrl: string;
  description: string;
  publishedDate: string;
  howToVideo?: string;
  howToVideoDescription?: string;
  howToVideoPublishedDate?: string;
}

export default function ActivitySchema({
  time,
  supplies,
  steps,
  notes,
  title,
  imageUrl,
  description,
  publishedDate,
  howToVideo,
  howToVideoDescription,
  howToVideoPublishedDate,
}: IHowToSchemaProps) {
  const { getDictionaryValue } = useDictionary();

  const totalTimeH = time?.totalTimeHours ?? '0',
    totalTimeM = time?.totalTimeMinutes ?? '0';

  const supplyList = ([] as string[]).concat(
    ...supplies.map((group) => {
      return group.suppliesList.map((supply) => {
        const quantity = supply.suppliesQuantity ?? '';
        return `${quantity} ${supply.suppliesName}`;
      });
    })
  );

  // Instructions can have multiple sections
  // Outputs into a separate array object
  const stepsSchema = steps.map((section) => {
    const stepsSection = {
      '@type': 'HowToSection',
      name: section.stepsSubheading ? section.stepsSubheading : getDictionaryValue('seo.steps'),
      position: 1,
      itemListElement: section.stepsList.map((step) => {
        const stepItem = {
          '@type': 'HowToStep',
          text: step.stepDescription,
        };
        return stepItem;
      }),
    };
    return stepsSection;
  });

  // Notes/Tips are optional
  let activityNotes = null;

  if (notes) {
    activityNotes = {
      '@type': 'HowToSection',
      name: getDictionaryValue('seo.tips'),
      position: 2,
      itemListElement: notes.map((note) => {
        const step = {
          '@type': 'HowToStep',
          text: note.noteItem,
        };
        return step;
      }),
    };
  }

  const stepValues = [stepsSchema].flat();

  // Add optional notes/tips to instructions
  if (activityNotes && activityNotes.itemListElement.length > 0) {
    stepValues.push(activityNotes);
  }

  const oembed = useOembed(howToVideo);

  // Add optional activity video
  const howToVideoValue = oembed
    ? {
        '@type': 'VideoObject',
        description: howToVideoDescription,
        contentUrl: howToVideo,
        uploadDate: howToVideoPublishedDate,
        name: oembed.title,
        thumbnailUrl: oembed.thumbnail_url,
      }
    : null;

  const schemaHowTo = {
    '@context': 'http://schema.org',
    '@type': 'HowTo',
    name: title,
    image: [imageUrl],
    description: description,
    author: {
      '@type': 'Organization',
      name: organization,
    },
    datePublished: publishedDate,
    totalTime: `PT${totalTimeH}H${totalTimeM}M`,
    supply: supplyList,
    step: stepValues,
    ...(howToVideo && { video: howToVideoValue }),
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaHowTo) }}
      />
    </Head>
  );
}
